import { useId } from "react";
import React from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./Accordion.module.scss";

export interface AccordionItem {
  id?: string;
  title: string;
  content: React.ReactNode;
}

type AccordionProps = {
  items?: AccordionItem[];
} & PlatformComponentProps &
  React.ComponentProps<typeof AccordionPrimitive.Root>;

type AccordionTriggerProps = PlatformComponentProps &
  React.ComponentProps<typeof AccordionPrimitive.Trigger>;

type AccordionContentProps = PlatformComponentProps &
  React.ComponentProps<typeof AccordionPrimitive.Content>;

const COMPONENT_KEY = CoreUiComponents.Accordion;

export const Accordion: React.FC<AccordionProps> = ({
  items,
  className,
  children,
  ...props
}) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const reactId = useId();

  return (
    <AccordionPrimitive.Root className={clsx(block, className)} {...props}>
      {items?.map(({ id, title, content }, index) => (
        <AccordionPrimitive.Item
          key={id ?? `${reactId}_${index}`}
          value={id ?? `${reactId}_${index}`}
          className={cn("item")}
        >
          <AccordionTrigger>{title}</AccordionTrigger>
          <AccordionContent>{content}</AccordionContent>
        </AccordionPrimitive.Item>
      ))}
      {children}
    </AccordionPrimitive.Root>
  );
};

export const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ children, className, ...props }, forwardedRef) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  return (
    <AccordionPrimitive.Header className={cn("header")} asChild>
      <AccordionPrimitive.Trigger
        className={clsx(cn("trigger"), className)}
        {...props}
        ref={forwardedRef}
      >
        {children}
        <Icon
          className={cn("trigger-icon")}
          iconName={IconNameEnum.ui__accordionAction}
          aria-hidden
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});

export const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ children, className, ...props }, forwardedRef) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  return (
    <AccordionPrimitive.Content
      className={clsx(cn("content"), className)}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </AccordionPrimitive.Content>
  );
});

export const AccordionItem = AccordionPrimitive.Item;
